import { useState } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { Check, ChevronsUpDown, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useSession } from '@/contexts/session-context';
import { useWorkspace, type OrgOption } from '@/contexts/workspace-context';
import { useFetch } from '@/hooks/use-fetch';
import { parseApiError } from '@/lib/api-error';
import { orgUrl } from '@/lib/org-url';
import {
  clearOrgNavigationPending,
  markOrgNavigationPending,
} from '@/lib/pending-org-navigation';

/**
 * SaaS multi-org switcher (top of the Organization-view sidebar). The session
 * is scoped to ONE org at a time (the WorkOS picker chooses it at login); this
 * control re-roots the session mid-flight without a logout via POST
 * /auth/switch-organization, the same re-seal the picker performs.
 *
 * Shares the workspace switcher's design (Combobox trigger + searchable popup)
 * for a consistent sidebar header across views. Org filtering is client-side
 * (the org list is small and already loaded in the workspace context — no
 * server search endpoint, unlike the workspace switcher's 100s of rows).
 *
 * Switching re-roots the session cookie to a different org, so it ends with a
 * HARD navigation (window.location.assign) to the new org URL — same as the
 * WorkOS hosted picker. A client-side navigate would race: flipping the
 * session org while the URL still holds the old :orgId makes OrgLayout refetch
 * the old org with the new cookie → cross-org 403 → bounce to a workspace. A
 * full reload boots every request with the new cookie AND the new URL in
 * lockstep, so there is no mismatch window and no stale-effect cascade.
 *
 * Renders for every org-holding user (AIT-241: hosts the "Create organization"
 * row) — only an unresolved (empty) org list gets no chrome.
 * `session.organizationId` is the org's HookMyApp publicId (org_...), so the
 * current org is matched by `publicId`; `switchOrganization` is called with
 * the same publicId (what the backend resolves).
 */
export function OrgSwitcher() {
  const { organizationId, switchOrganization } = useSession();
  const { organizations, refreshWorkspaces } = useWorkspace();
  const fetchWithAuth = useFetch();
  const [switching, setSwitching] = useState(false);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  // Set once POST /organizations succeeds. From that point the dialog only
  // retries the SWITCH — never a second create (duplicate-org guard).
  const [createdOrg, setCreatedOrg] = useState<{ publicId: string; name: string } | null>(null);

  // Renders for every org-holding user (AIT-241: hosts the create row).
  // Length 0 = org list unresolved — no chrome.
  if (organizations.length === 0) return null;

  const current = organizations.find((o) => o.publicId === organizationId);

  async function handleSwitch(org: OrgOption) {
    if (switching || org.publicId === organizationId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    setOpen(false);
    try {
      // Re-seal the cookie to the target org, then HARD-navigate to that org's
      // own Organization view. The full reload re-bootstraps every context
      // against the new cookie + new URL together (see component JSDoc) — a
      // client-side navigate would race the cookie/URL flip into a cross-org
      // 403 bounce. No setSwitching(false) needed: the page is reloading.
      //
      // AIT-539: the re-seal lands BEFORE the navigation, so views still
      // holding the old :orgId 403 in between. Mark the navigation first so
      // they wait for it instead of reading that 403 as a denial.
      markOrgNavigationPending(org.publicId);
      await switchOrganization(org.publicId);
      window.location.assign(orgUrl(org.publicId));
    } catch {
      clearOrgNavigationPending();
      toast.error('Could not switch organization. Please try again.');
      setSwitching(false);
    }
  }

  async function switchToCreated(publicId: string) {
    // Same pre-navigation marker as handleSwitch (AIT-539); the create dialog's
    // catch below clears it when the re-seal fails.
    markOrgNavigationPending(publicId);
    await switchOrganization(publicId);
    window.location.assign(orgUrl(publicId));
  }

  async function handleCreateSubmit() {
    if (creating) return;
    setCreating(true);

    // Phase 1 — CREATE (skipped in recovery mode). Its failures must never
    // enter switch-failure recovery: an un-created org has nothing to retry.
    let target = createdOrg;
    if (!target) {
      let data: { organization: { publicId: string; name: string } };
      try {
        const res = await fetchWithAuth('/organizations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName }),
        });
        if (!res.ok) {
          // AppError responses carry the safe copy as `message` (never
          // `userMessage` on the wire) — surface via parseApiError like the
          // rest of the app.
          const body: unknown = await res.json().catch(() => null);
          toast.error(
            parseApiError(res.status, body, 'Could not create organization. Please try again.')
              .userMessage,
          );
          setCreating(false);
          return;
        }
        data = (await res.json()) as {
          organization: { publicId: string; name: string };
          workspace: { id: string; name: string };
        };
      } catch {
        toast.error('Could not create organization. Please try again.');
        setCreating(false);
        return;
      }
      target = data.organization;
      setCreatedOrg(target);
    }

    // Phase 2 — SWITCH (the only retryable phase; POST is never repeated).
    try {
      await switchToCreated(target.publicId);
      // No setCreating(false): the page is reloading (see handleSwitch).
    } catch {
      // Org exists; only the session re-seal failed. Refresh the org list so
      // the new org is selectable, keep the dialog in retry-switch mode.
      clearOrgNavigationPending();
      await refreshWorkspaces().catch(() => undefined);
      toast.error('Organization created, but switching failed. Try again.');
      setCreating(false);
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Combobox.Root
          items={organizations}
          open={open}
          onOpenChange={setOpen}
          onValueChange={(item) => {
            if (item) void handleSwitch(item as OrgOption);
          }}
          itemToStringLabel={(o: OrgOption) => o?.name ?? ''}
          isItemEqualToValue={(a: OrgOption, b: OrgOption) =>
            a?.publicId === b?.publicId
          }
        >
          <Combobox.Trigger
            render={
              <SidebarMenuButton
                size="lg"
                aria-label="Switch Organization"
                className="h-12 rounded-none px-4 group-data-[collapsible=icon]:px-2 data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              />
            }
          >
            <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-semibold shrink-0">
              {switching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                (current?.name ?? 'O').charAt(0).toUpperCase()
              )}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-semibold">
                {current?.name ?? 'Organization'}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                Organization
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
          </Combobox.Trigger>

          <Combobox.Portal>
            <Combobox.Positioner positionMethod="fixed" className="z-50" align="start" side="bottom" sideOffset={4}>
              {/* Fixed width so the popup stays usable in BOTH the expanded
                  (16rem) sidebar and the collapsed icon rail (4rem) — anchor-width
                  math would shrink it to the rail width otherwise. */}
              <Combobox.Popup className="z-50 w-60 rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95">
                <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
                  <Search className="size-4 shrink-0 text-muted-foreground" />
                  <Combobox.Input
                    placeholder="Find organization"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <div className="-mx-1 my-1 h-px bg-foreground/10" />

                <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                  No organizations match your search
                </Combobox.Empty>

                <Combobox.List className="max-h-52 overflow-y-auto no-visible-scrollbar">
                  {(item: OrgOption) => (
                    <Combobox.Item
                      key={item.publicId}
                      value={item}
                      className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                    >
                      <div className="flex size-6 items-center justify-center rounded-sm bg-primary text-primary-foreground text-xs font-semibold">
                        {item.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="flex-1 truncate">{item.name}</span>
                      {item.publicId === organizationId && (
                        <Check className="size-4 shrink-0" />
                      )}
                    </Combobox.Item>
                  )}
                </Combobox.List>

                <div className="-mx-1 my-1 h-px bg-foreground/10" />
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setCreateOpen(true);
                  }}
                  disabled={creating}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  <Plus className="size-4" />
                  Create organization
                </button>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>

        <Dialog open={createOpen} onOpenChange={(o) => !creating && setCreateOpen(o)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create organization</DialogTitle>
              <DialogDescription>
                {createdOrg
                  ? `"${createdOrg.name}" was created. Switch to it to continue.`
                  : 'A new organization starts with its own workspace.'}
              </DialogDescription>
            </DialogHeader>
            {!createdOrg && (
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Organization name"
                aria-label="Organization name"
                maxLength={120}
                disabled={creating}
              />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleCreateSubmit()}
                disabled={creating || (!createdOrg && !newName.trim())}
              >
                {createdOrg ? 'Switch to organization' : creating ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
